import { Link } from '../routes';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

/**
 * 🔴 THE SCREEN A DISABLED MODULE ANSWERS WITH — ERP Phase 1a increment 4.
 *
 * §1a's second exit criterion, in its own words: *"a screen behind a disabled module is absent from
 * the nav and answers the module-disabled state, **not a blank page**"*. The roadmap names the
 * failure it expects, which is what makes this a criterion rather than a preference.
 *
 * ## ⚠️ WHO ACTUALLY ARRIVES HERE
 *
 * Not somebody browsing — the nav does not offer these links. This is reached by a **bookmark, a
 * shared URL, or a link in an email** from back when the module was on, or from a colleague at a
 * company that has it. 🔬 So the copy is written for a person who had a reason to expect the page to
 * work, and the useful thing to tell them is not *"no"* but **who can turn it on**.
 *
 * ## 🔴 IT DOES NOT OFFER A BUTTON TO ENABLE IT
 *
 * ⚠️ Enabling a module is a licensing decision with a price attached, and the person who followed a
 * stale bookmark is usually not the person who makes it. A button here would either fail for most
 * people who pressed it or quietly change what a company is billed for. 🔬 It names the module and
 * points at Settings, where somebody with `modules:write` can do it deliberately.
 */
export function ModuleDisabledScreen({ module, from }: { module: string; from?: string }) {
  useDocumentTitle(`${label(module)} is not turned on`); // DS-11b1: the tab names the page (2.4.2)
  return (
    <div className="wb-empty" role="status">
      <h1 className="wb-empty__title">{label(module)} is not turned on</h1>
      <p className="wb-empty__body">
        This company does not have the <strong>{label(module)}</strong> module enabled, so the page
        you asked for is not available here.
        {from ? (
          <>
            {' '}
            You asked for <code>{from}</code> — most likely a bookmark or a link from when it was on.
          </>
        ) : null}
      </p>
      <p className="wb-empty__body">
        Somebody with permission to manage modules can turn it on in{' '}
        <Link to="/settings" className="doc-link">
          Settings
        </Link>
        . Until then nothing behind it is reachable, and no data has been lost — a module that is off
        keeps everything it recorded while it was on.
      </p>
    </div>
  );
}

/**
 * 🔬 THE MODULE KEY IS NOT THE MODULE'S NAME. `warranty_care` and `fixed_assets` are database keys,
 * and putting one in front of a shopkeeper is the product speaking its own internals aloud.
 *
 * ⚠️ Deliberately a plain transformation rather than a table copied from the server's catalogue: a
 * second copy of those names drifts from the first, and this is the ONE place the web renders a
 * module key at all. When 1a.5 gives the shell its i18n keys, the label comes from there instead.
 */
function label(module: string): string {
  const words = module.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}
